import AnimationClip from '../AnimationClip';
import GameObject from '../../../GameObject/GameObject';
import { delay } from '../../../Utility/Task/TaskQueue';
import { Easing, Tween } from '@tweenjs/tween.js';
import { AwaitIterators, StartAnimation } from '../AnimationHelper';
import TransformComponent from '../../../Component/Transform';
import { RenderSystemEvent } from '../../Render/RenderSystem';
import { AnimationSystemEvent } from '../AnimationSystem';
import ColorFilter from '../../../Component/ColorFilter';
import SpriteComponent from '../../../Component/Render/Sprite';
import Container from '../../../Prototype/Container';
import Renderable from '../../../Component/Renderable';

export class SurpriseCardChoose extends AnimationClip {
    public *execute(
        gameObject: GameObject,
        meta: any
    ): IterableIterator<number> {
        meta.card.emit(RenderSystemEvent.BRING_TO_FRONT);
        meta.card.emit(AnimationSystemEvent.STOP);

        // Move card to center and scale it.
        yield* this.tweenCard(meta.card);

        // Transform surprise card to back card.
        yield* this.transformInto(meta.card, meta.ray);

        // Wait 1 second until move to card collections.
        yield* delay(1000);

        // Fade out ray.
        const sprite = meta.ray.getComponent(SpriteComponent);
        const tweenAlpha = this.getTween(sprite);
        tweenAlpha.to({ alpha: 0 }, 100).start(this.elapsedTime);

        // Get card tab position.
        const tab: GameObject = GameObject.findByName('social_section')[0];
        const fakeNode: GameObject = GameObject.Instantiate(Container);
        tab.addChild(fakeNode);
        meta.card.parent.addChild(fakeNode);
        const nodeTransform = fakeNode.getComponent(TransformComponent);
        fakeNode.destroy();

        // Move card to side menu card tab.
        yield* this.moveCardToTab(meta.card, nodeTransform, 800);
    }

    private *tweenCard(card: GameObject): IterableIterator<number> {
        const transform: TransformComponent = card.getComponent(
            TransformComponent
        );
        transform.rotation = 0;

        const moveTween: Tween = this.getTween(transform.position);
        const scaleTween: Tween = this.getTween(transform.scale);

        moveTween.to({ x: 0, y: 0 }, 250).easing(Easing.Quadratic.Out);

        scaleTween.to({ x: 1.25, y: 1.25 }, 250);

        yield* AwaitIterators([
            this.startTween(moveTween),
            this.startTween(scaleTween),
        ]);
    }

    private *transformInto(
        card: GameObject,
        ray: GameObject
    ): IterableIterator<number> {
        const transform: TransformComponent = card.getComponent(
            TransformComponent
        );

        const scaleTween: Tween = this.getTween(transform.scale);

        let colorFilter: ColorFilter = card.getComponent(ColorFilter);
        if (colorFilter) {
            colorFilter.brightness = 1;
        } else {
            colorFilter = card.addComponent<ColorFilter>(ColorFilter, {
                brightness: 1,
            });
        }

        const colorTween: Tween = this.getTween(colorFilter);

        scaleTween.to({ x: 0.75, y: 0.75 }, 500);

        colorTween.to({ brightness: 50 }, 500);

        yield* AwaitIterators([
            this.shake(card),
            this.startTween(scaleTween),
            this.startTween(colorTween),
        ]);

        const rayTransform: TransformComponent = ray.getComponent(
            TransformComponent
        );
        const tweenScale: Tween = this.getTween(rayTransform.scale);
        tweenScale.to({ x: 3, y: 3 }, 500).start(this.elapsedTime);

        yield* delay(100);

        card.children[1].getComponent(SpriteComponent).visible = true;

        scaleTween.to({ x: 1.25, y: 1.25 }, 500).easing(Easing.Back.Out);

        colorTween.to({ brightness: 1 }, 300);

        yield* AwaitIterators([
            this.startTween(scaleTween),
            this.startTween(colorTween),
        ]);
    }

    private *shake(card: GameObject): IterableIterator<number> {
        const transform: TransformComponent = card.getComponent(
            TransformComponent
        );

        const delay: number = 75;
        const rot1 = Math.PI * 0.05;
        const rot2 = Math.PI * -0.05;
        const rotT = 0;

        const tween: Tween = this.getTween(transform);
        tween.easing(Easing.Quadratic.InOut);

        yield* this.rotateTo(tween, rot2, delay);
        yield* this.rotateTo(tween, rot1, delay);
        yield* this.rotateTo(tween, rot2, delay);
        yield* this.rotateTo(tween, rot1, delay);
        yield* this.rotateTo(tween, rot2, delay);
        yield* this.rotateTo(tween, rot1, delay);
        yield* this.rotateTo(tween, rotT, delay);
    }

    private *rotateTo(
        tween: Tween,
        rotation: number,
        delay: number
    ): IterableIterator<number> {
        tween.to({ rotation: rotation }, delay);
        yield* this.startTween(tween);
    }

    private *moveCardToTab(
        card: GameObject,
        tabTransform: TransformComponent,
        duration: number
    ) {
        const transform: TransformComponent = card.getComponent(
            TransformComponent
        );
        const renderable: Renderable = card.getComponent(Renderable);

        const tweenPosition: Tween = this.getTween(transform.position);
        const tweenRotation: Tween = this.getTween(transform);
        const tweenScale: Tween = this.getTween(transform.scale);
        const tweenFade: Tween = this.getTween(renderable);

        tweenPosition
            .to({ x: tabTransform.x + 132, y: tabTransform.y + 54 }, duration)
            .easing(Easing.Cubic.In);

        tweenRotation.to({ rotation: -Math.PI * 2 }, duration);

        tweenScale.to({ x: 0.1, y: 0.1 }, duration).easing(Easing.Cubic.In);

        tweenFade.to({ alpha: 0 }, duration + 100).easing(Easing.Cubic.In);

        yield* AwaitIterators([
            this.startTween(tweenPosition),
            this.startTween(tweenRotation),
            this.startTween(tweenScale),
            this.startTween(tweenFade),
        ]);
    }
}
